'use client';

import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { ChevronLeft, ChevronRight, Info, Search, SlidersHorizontal, X } from 'lucide-react';
import AIScansShell from '@/components/ai-scans/AIScansShell';
import ActiveFilterChips from '@/components/ai-scans/ActiveFilterChips';
import CopyButton from '@/components/ai-scans/CopyButton';
import ErrorState from '@/components/ai-scans/ErrorState';
import ScreenerTable from '@/components/ai-scans/ScreenerTable';
import StarterScans, { StarterScan } from '@/components/ai-scans/StarterScans';
import { formatCount, formatDateTime, timeAgo } from '@/components/ai-scans/format';
import { useScreenerMeta } from '@/components/ai-scans/useScreenerMeta';
import IndustryDropdown from '@/components/ui/IndustryDropdown';
import { getScreenerStocks } from '@/lib/api/screenerApi';
import type { ScreenerColumn, ScreenerQueryParams, ScreenerStocksResponse } from '@/types/screener';

const PAGE_SIZE = 50;
const DEFAULT_VIEW = 'overview';
const DEFAULT_SORT = 'market_cap_cr';
const NON_FILTER_PARAMS = new Set(['view', 'sort', 'order', 'page']);
const EXCHANGES = [
  { value: '', label: 'All' },
  { value: 'NSE', label: 'NSE' },
  { value: 'BSE', label: 'BSE' },
];

function ScreenerContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const { meta, error: metaError, columnsByKey, retry: retryMeta } = useScreenerMeta();

  const queryString = searchParams.toString();
  const view = searchParams.get('view') || DEFAULT_VIEW;
  const sort = searchParams.get('sort') || DEFAULT_SORT;
  const order = searchParams.get('order') === 'asc' ? 'asc' : 'desc';
  const page = Math.max(1, Number.parseInt(searchParams.get('page') || '1', 10) || 1);
  const industry = searchParams.get('industry') ?? '';
  const exchange = searchParams.get('exchange') ?? '';
  const urlQuery = searchParams.get('q') ?? '';

  const [result, setResult] = useState<ScreenerStocksResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const resultsRef = useRef<HTMLDivElement>(null);

  // The URL is the single source of truth, so any scan can be shared as a link.
  const updateParams = useCallback(
    (changes: Record<string, string | null>, keepPage = false) => {
      const next = new URLSearchParams(queryString);
      Object.entries(changes).forEach(([key, value]) => {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      });
      if (!keepPage) next.delete('page');
      const nextQuery = next.toString();
      router.replace(nextQuery ? `${pathname}?${nextQuery}` : pathname, { scroll: false });
    },
    [queryString, router, pathname],
  );

  const [searchText, setSearchText] = useState(urlQuery);
  const pushedQuery = useRef(urlQuery);

  useEffect(() => {
    // Follow URL changes we didn't make ourselves (chip removed, starter scan picked).
    if (urlQuery !== pushedQuery.current) {
      pushedQuery.current = urlQuery;
      setSearchText(urlQuery);
    }
  }, [urlQuery]);

  useEffect(() => {
    const trimmed = searchText.trim();
    if (trimmed === urlQuery) return;
    const timer = setTimeout(() => {
      pushedQuery.current = trimmed;
      updateParams({ q: trimmed || null });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchText, urlQuery, updateParams]);

  useEffect(() => {
    setShareUrl(window.location.href);
  }, [queryString]);

  useEffect(() => {
    let cancelled = false;
    const params: ScreenerQueryParams = {};
    new URLSearchParams(queryString).forEach((value, key) => {
      if (key !== 'page') params[key] = value;
    });
    params.limit = PAGE_SIZE;
    params.offset = (page - 1) * PAGE_SIZE;

    setLoading(true);
    setError(null);
    getScreenerStocks(params)
      .then((response) => {
        if (!cancelled) setResult(response);
      })
      .catch((err) => {
        if (!cancelled) setError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [queryString, page, attempt]);

  const currentView = meta?.views.find((item) => item.id === view);
  const tableColumns = useMemo(() => {
    const keys = result?.columns ?? currentView?.columns ?? [];
    return keys
      .map((key) => columnsByKey.get(key))
      .filter((column): column is ScreenerColumn => Boolean(column));
  }, [result, currentView, columnsByKey]);

  const handleSort = (key: string) => {
    const nextOrder = sort === key ? (order === 'desc' ? 'asc' : 'desc') : key === 'name' ? 'asc' : 'desc';
    updateParams({ sort: key, order: nextOrder });
  };

  const handleView = (viewId: string) => {
    const nextView = meta?.views.find((item) => item.id === viewId);
    const sortStillVisible = sort === 'name' || sort === DEFAULT_SORT || nextView?.columns.includes(sort);
    updateParams({
      view: viewId === DEFAULT_VIEW ? null : viewId,
      ...(sortStillVisible ? {} : { sort: null, order: null }),
    });
  };

  const handleStarterScan = (scan: StarterScan | null) => {
    const query = scan ? new URLSearchParams(scan.params).toString() : '';
    router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
  };

  const clearFilters = () => {
    router.replace(view === DEFAULT_VIEW ? pathname : `${pathname}?view=${view}`, { scroll: false });
  };

  const goToPage = (nextPage: number) => {
    updateParams({ page: nextPage > 1 ? String(nextPage) : null }, true);
    resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const total = result?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const firstRow = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const lastRow = Math.min(page * PAGE_SIZE, total);
  const hasFilters = Array.from(searchParams.keys()).some((key) => !NON_FILTER_PARAMS.has(key));
  const mobileFilterCount = (industry ? 1 : 0) + (exchange ? 1 : 0);
  const marketSynced = result?.last_synced.market ?? meta?.last_synced.market;

  return (
    <>
      <header className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900 md:text-3xl dark:text-white">Stock Screener</h1>
          <p className="mt-1 max-w-2xl text-sm text-zinc-500 md:text-base dark:text-zinc-400">
            Every listed Indian company in one table. Pick a view, sort any column, and tap{' '}
            <Info className="inline h-3.5 w-3.5 align-[-2px]" /> next to a column name to learn what it means.
          </p>
        </div>
        {shareUrl && <CopyButton text={shareUrl} label="Copy link to this scan" copiedLabel="Link copied!" />}
      </header>

      <div className="mt-6">
        <StarterScans searchParams={searchParams} onSelect={handleStarterScan} />
      </div>

      <section className="mt-6 rounded-2xl border border-zinc-200 bg-white p-3 sm:p-4 dark:border-zinc-800 dark:bg-zinc-900">
        <div
          role="tablist"
          aria-label="Column views"
          className="-mx-3 flex gap-1 overflow-x-auto px-3 sm:mx-0 sm:flex-wrap sm:px-0 [scrollbar-width:none]"
        >
          {meta
            ? meta.views.map((item) => {
                const active = item.id === view;
                return (
                  <button
                    key={item.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => handleView(item.id)}
                    className={`shrink-0 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                      active
                        ? 'bg-zinc-900 text-white dark:bg-white dark:text-zinc-900'
                        : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-white'
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })
            : Array.from({ length: 8 }, (_, index) => (
                <span key={index} className="h-8 w-24 shrink-0 animate-pulse rounded-lg bg-zinc-100 dark:bg-zinc-800" />
              ))}
        </div>
        {currentView && <p className="mt-2 px-1 text-sm text-zinc-500 dark:text-zinc-400">{currentView.description}</p>}

        <div className="mt-4 flex flex-col gap-3 border-t border-zinc-100 pt-4 md:flex-row md:items-center dark:border-zinc-800">
          <div className="flex gap-2 md:flex-1">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
              <input
                type="search"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search company or symbol…"
                aria-label="Search stocks"
                className="w-full rounded-lg border border-zinc-300 bg-white py-2 pl-9 pr-9 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white"
              />
              {searchText && (
                <button
                  type="button"
                  onClick={() => setSearchText('')}
                  aria-label="Clear search"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowMobileFilters((open) => !open)}
              aria-expanded={showMobileFilters}
              className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 px-3 text-sm font-medium text-zinc-700 md:hidden dark:border-zinc-700 dark:text-zinc-300"
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filters
              {mobileFilterCount > 0 && (
                <span className="rounded-full bg-emerald-600 px-1.5 text-xs font-semibold text-white">{mobileFilterCount}</span>
              )}
            </button>
          </div>

          <div className={`${showMobileFilters ? 'flex' : 'hidden'} flex-col gap-3 sm:flex-row sm:items-center md:flex`}>
            <div className="sm:w-64">
              <IndustryDropdown
                value={industry}
                onChange={(value) => updateParams({ industry: value || null })}
                options={meta?.industries.map((item) => item.name) ?? []}
                placeholder="All industries"
                disabled={!meta}
              />
            </div>
            <div
              role="radiogroup"
              aria-label="Exchange"
              className="inline-flex shrink-0 self-start rounded-lg border border-zinc-300 p-0.5 sm:self-auto dark:border-zinc-700"
            >
              {EXCHANGES.map((option) => {
                const active = exchange === option.value;
                return (
                  <button
                    key={option.label}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => updateParams({ exchange: option.value || null })}
                    className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                      active
                        ? 'bg-emerald-600 text-white'
                        : 'text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-3 empty:hidden">
          <ActiveFilterChips
            searchParams={searchParams}
            columnsByKey={columnsByKey}
            onRemove={(param) => updateParams({ [param]: null })}
            onClearAll={clearFilters}
          />
        </div>
      </section>

      <div ref={resultsRef} className="mt-4 flex scroll-mt-24 flex-wrap items-center justify-between gap-2 text-sm text-zinc-500 dark:text-zinc-400">
        <span aria-live="polite">
          {result && !error ? (
            total > 0 ? (
              <>
                Showing <strong className="font-semibold text-zinc-800 dark:text-zinc-200">{formatCount(firstRow)}–{formatCount(lastRow)}</strong> of{' '}
                <strong className="font-semibold text-zinc-800 dark:text-zinc-200">{formatCount(total)}</strong> stocks
              </>
            ) : (
              'No stocks found'
            )
          ) : (
            ' '
          )}
        </span>
        {timeAgo(marketSynced) && (
          <span title={formatDateTime(marketSynced) ?? undefined}>Prices updated {timeAgo(marketSynced)}</span>
        )}
      </div>

      <div className="mt-2">
        {metaError ? (
          <ErrorState error={metaError} onRetry={retryMeta} />
        ) : error ? (
          <ErrorState error={error} onRetry={() => setAttempt((n) => n + 1)} />
        ) : (
          <ScreenerTable
            columns={tableColumns}
            rows={result?.items ?? []}
            sort={sort}
            order={order}
            loading={loading || !meta}
            onSort={handleSort}
            hasFilters={hasFilters}
            onClearFilters={clearFilters}
          />
        )}
      </div>

      {!error && total > PAGE_SIZE && (
        <nav aria-label="Pages" className="mt-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => goToPage(page - 1)}
            disabled={page <= 1 || loading}
            className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:border-emerald-500/50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </button>
          <span className="text-sm text-zinc-500 dark:text-zinc-400">
            Page {formatCount(page)} of {formatCount(totalPages)}
          </span>
          <button
            type="button"
            onClick={() => goToPage(page + 1)}
            disabled={page >= totalPages || loading}
            className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm font-medium text-zinc-700 transition-colors hover:border-emerald-500/50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        </nav>
      )}
    </>
  );
}

export default function ScreenerPage() {
  return (
    <AIScansShell>
      <Suspense fallback={<div className="h-96 animate-pulse rounded-2xl bg-zinc-100 dark:bg-zinc-900" />}>
        <ScreenerContent />
      </Suspense>
    </AIScansShell>
  );
}
